import axios from 'axios';
import { ActionTree, GetterTree, Module, MutationTree } from 'vuex';
import { namespace } from 'vuex-class';

import { addDays, subtractDays, toNativeDateTime, toShortDate } from '@/common/datetime';
import { DailyReport } from '@/models/dailyReport.model';
import { Fuel } from '@/models/fuel.model';

import { RootState } from './index';


export const EDITOR_WIDTH = '66rem';


/**
 *
 * State
 *
 */
export interface DailyReportState {
  endDate: string;
  isFetching: boolean;
  isTankFieldsShown: boolean;
  startDate: string;
  values: DailyReport[];
}

const makeInitialState = (): DailyReportState => ({
  endDate: toShortDate(new Date()),
  isFetching: false,
  isTankFieldsShown: getIsTankFieldsShown(),
  startDate: toShortDate(subtractDays(new Date(), 30)),
  values: [],
});

// Helper function for checkbox backwards compatibility  (v1.1 -> v1.2).
function getIsTankFieldsShown() {
  return localStorage.getItem('isHeightShown') === 'true' ||
    localStorage.getItem('isTankFieldsShown') === 'true';
}

const state = makeInitialState;


/**
 *
 * Getters
 *
 */
interface DailyReportGetters {
  byFuel: { [key: string]: Fuel };
}

const getters: GetterTree<DailyReportState, RootState> = {
  byFuel(state) {
    return state.values.reduce((result, report) => {
      if (!result[report.fuelId]) {
        result[report.fuelId] = [];
      }
      result[report.fuelId].push(report);
      return result;
    }, {});
  },
};


/**
 *
 * Mutations
 *
 */
interface DailyReportMutation {
  setTankFields: (value: boolean) => void;
}

const mutations: MutationTree<DailyReportState> = {
  reset(state) {
    const initialState = makeInitialState();
    for (const key in initialState) {
      if (initialState.hasOwnProperty(key)) {
        state[key] = initialState[key];
      }
    }
  },

  setEndDate(state, value: string) {
    state.endDate = value;
  },

  setFetching(state) {
    state.isFetching = true;
  },

  setTankFields(state, value: boolean) {
    state.isTankFieldsShown = value;

    // Remove old key, if it still exists.
    localStorage.removeItem('isHeightShown');

    if (value) {
      localStorage.setItem('isTankFieldsShown', 'true');
    }
    else {
      localStorage.removeItem('isTankFieldsShown');
    }
  },

  setStartDate(state, value: string) {
    state.startDate = value;
  },

  setValues(state, values: DailyReport[]) {
    state.values = values;
    state.isFetching = false;
  },

  insertValues(state, updatedReports: DailyReport[]) {
    let newValues = state.values;

    updatedReports.forEach(report => {
      const i = state.values.findIndex(m => m.date === report.date && m.fuelId === report.fuelId);
      if (i >= 0) {
        newValues = [...newValues.slice(0, i), report, ...newValues.slice(i + 1)];
      }
      else {
        newValues = [...newValues, report];
      }
    });

    state.values = newValues.sort((l, r) => l.date.localeCompare(r.date));
  },

  removeValues(state, removedReports: DailyReport[]) {
    state.values = state
      .values
      .filter(report => !removedReports.find(m => m.date === report.date && m.fuelId === report.fuelId))
      .sort((l, r) => l.date.localeCompare(r.date));
  },
};


/**
 *
 * Actions
 *
 */
interface DailyReportActions {
  create: (payload: { reports: DailyReport[] }) => Promise<any>;
  delete: (payload?: { reports: DailyReport[] }) => Promise<void>;
  fetch: (payload?: { startDate: string; endDate: string }) => Promise<void>;
  makeDefaultDailyReportForFuel: (fuel: Fuel) => Promise<DailyReport>;
  update: (payload: { oldReports: DailyReport[]; newReports: DailyReport[] }) => Promise<any>;
}

const actions: ActionTree<DailyReportState, RootState> = {
  /**
   * Create a default (blank with next-available date) entry.
   */
  async makeDefaultDailyReportForFuel(store, fuel: Fuel): Promise<DailyReport> {
    const allDates = store.state.values
      .map(report => report.date)
      .sort();

    const latestDate = allDates.length
      ? toNativeDateTime(`${allDates[allDates.length - 1]}T00:00:00`)
      : null;

    const date = latestDate instanceof Date && !isNaN(latestDate as any)
      ? addDays(latestDate, 1)
      : new Date();

    return Promise.resolve({
      companyId: store.rootState.company.companyId,
      date: toShortDate(date),
      deliveries: null,
      fuelId: fuel.fuelId,
      locationId: store.rootState.location.locationId,
      processed: false,
      sales: null,
      tanks: fuel.tanks.map(tank => ({
        productLevel: null,
        tankId: tank.tankId,
        tankVolume: null,
        temperature: null,
        waterLevel: null,
      })),
      volume: null,
    });
  },

  /**
   * Fetch all daily report entries between the given dates.
   */
  async fetch(store, payload?: { startDate: string; endDate: string }) {
    store.commit('setFetching');

    if (payload && payload.startDate) {
      store.commit('setStartDate', payload.startDate);
    }
    if (payload && payload.endDate) {
      store.commit('setEndDate', payload.endDate);
    }

    const companyId = store.rootState.company.companyId;
    const locationId = store.rootState.location.locationId;
    const params = `companyId=${companyId}&locationId=${locationId}&startDate=${store.state.startDate}&endDate=${store.state.endDate}`;

    const promises = store.rootState.location.fuels.map(fuel => (
      axios.get<DailyReport[]>(`dailyreports?${params}&fuelSystemId=${fuel.fuelId}`)
    ));

    try {
      const result = await Promise.all(promises);
      const resultsFlat = result.reduce((ret, reports) => {
        return [...ret, ...reports.data];
      }, []);
      store.commit('setValues', resultsFlat);
    }
    catch (ex) {
      console.warn(ex);
      store.commit('setValues', []);
    }
  },

  /**
   * Create a batch of new daily report entries.
   */
  async create(store, payload: { reports: DailyReport[] }) {
    const url = 'dailyreport';

    const promises = payload.reports.map(report => axios.post(url, report));
    await Promise.all(promises);

    store.commit('insertValues', payload.reports);
  },

  /**
   * Save a batch of daily report entries.
   */
  async update(store, payload: { oldReports: DailyReport[]; newReports: DailyReport[] }) {
    const url = 'dailyreport';

    const promises = payload.newReports.map((report, i) => {
      const requestBody = { oldValue: payload.oldReports[i], newValue: report };
      return axios.put(url, requestBody);
    });

    await Promise.all(promises);

    store.commit('removeValues', payload.oldReports);
    store.commit('insertValues', payload.newReports);
  },

  /**
   * Remove a batch of daily report entries.
   */
  async delete(store, payload: { reports: DailyReport[] }) {
    const url = 'dailyreport';

    const promises = payload.reports.map(m => {
      const deleteUrl = `${url}?companyId=${m.companyId}&locationId=${m.locationId}&fuelSystemId=${m.fuelId}&date=${m.date}`;
      return axios.delete(deleteUrl);
    });

    await Promise.all(promises);

    store.commit('removeValues', payload.reports);
  },
};


export default {
  namespaced: true,
  actions,
  getters,
  mutations,
  state,
} as Module<DailyReportState, RootState>;


// eslint-disable-next-line @typescript-eslint/no-namespace
export namespace DailyReportStore {
  export const Bind = namespace('dailyReport');
  export type Action <TProp extends keyof DailyReportActions> = DailyReportActions[TProp];
  export type Getters <TProp extends keyof DailyReportGetters> = DailyReportGetters[TProp];
  export type Mutation <TProp extends keyof DailyReportMutation> = DailyReportMutation[TProp];
  export type State <TProp extends keyof DailyReportState> = DailyReportState[TProp];
}

